import Spinner from "@/components/common/Spinner";
import {
    AUDIT_LOG_CSV_COLUMNS,
    auditLogRowToCsv,
    buildAuditLogExportFilename,
    fetchAllAuditLogForExport,
} from "@/features/admin/api/auditLogCsv";
import type { AuditLogEnrichedRow } from "@/features/admin/api/selectAuditLog";
import { AuditLogDetailSheet } from "@/features/admin/components/AuditLogDetailSheet";
import { AuditLogTable } from "@/features/admin/components/AuditLogTable";
import { parseCsv } from "@/features/admin/audit/auditFilterSearch";
import { useAuditLog } from "@/features/admin/hooks/useAuditLog";
import { useAuditLogSharedState } from "@/features/admin/hooks/useAuditLogSharedState";
import { exportMultipleToCsv, exportToCsv } from "@/utils/exportToCsv";
import { Button } from "@patient-watch/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ChevronLeft, ChevronRight, Download, History } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";

dayjs.extend(utc);

export const Route = createFileRoute("/_app/admin/audit/change-timeline")({
    component: AuditLogTimelinePage,
});

const PAGE_SIZE = 50;

function AuditLogTimelinePage() {
    const toastCopy = useToastCopy();
    const { search, resolvedScope, scopeSummary, defaultFromIso } = useAuditLogSharedState();

    const [page, setPage] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedRow, setSelectedRow] = useState<AuditLogEnrichedRow | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const diaryIdsKey = resolvedScope?.diaryIds?.join(",") ?? "";
    const diaryScopeEmpty = resolvedScope?.diaryScopeEmpty ?? false;

    const exportFilters = useMemo(
        () => ({
            tableNames: parseCsv(search.table),
            from: search.changedFrom ?? defaultFromIso,
            to: search.changedTo,
            diaryIds: resolvedScope?.diaryIds,
            diaryScopeEmpty,
        }),
        [search.table, search.changedFrom, search.changedTo, diaryScopeEmpty, resolvedScope?.diaryIds, defaultFromIso]
    );

    useEffect(() => {
        setPage(0);
    }, [search.table, search.changedFrom, search.changedTo, diaryIdsKey, diaryScopeEmpty, defaultFromIso]);

    const filters = useMemo(
        () => ({
            ...exportFilters,
            page,
            pageSize: PAGE_SIZE,
        }),
        [exportFilters, page]
    );

    const { data, isLoading, error, isFetching } = useAuditLog(filters);
    const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

    const exportCurrentPage = () => {
        if (!data?.rows.length) {
            toast.error(toastCopy.noRowsToExport);
            return;
        }

        const csvRows = data.rows.map(auditLogRowToCsv);
        exportToCsv(
            csvRows,
            `${buildAuditLogExportFilename(exportFilters, parseCsv(search.labels))}-page-${page + 1}`,
            AUDIT_LOG_CSV_COLUMNS
        );
        toast.success(toastCopy.exportedChangeEventsFromPage(csvRows.length));
    };

    const exportAllFiltered = async () => {
        setIsExporting(true);
        try {
            const result = await fetchAllAuditLogForExport(exportFilters);
            if (result.rows.length === 0) {
                toast.error(toastCopy.noChangeEventsMatchFilters);
                return;
            }

            const csvRows = result.rows.map(auditLogRowToCsv);
            const exportedAt = `${dayjs.utc().format("YYYY-MM-DD HH:mm:ss")} UTC`;

            exportMultipleToCsv<Record<string, unknown>>(
                [
                    {
                        title: "Export metadata",
                        data: [
                            {
                                exported_at_utc: exportedAt,
                                export_type: "change_timeline",
                                scope: scopeSummary ?? "All diaries",
                                filter_tables: exportFilters.tableNames?.join(", ") ?? "all",
                                filter_from_utc: exportFilters.from ?? "",
                                filter_to_utc: exportFilters.to ?? "",
                                diary_count: resolvedScope?.diaryIds?.length ?? "all",
                                total_matching_rows: result.total,
                                rows_in_file: result.rows.length,
                                truncated: result.truncated ? "yes" : "no",
                            },
                        ],
                    },
                    { title: "Change timeline", data: csvRows },
                ],
                buildAuditLogExportFilename(exportFilters, parseCsv(search.labels))
            );

            if (result.truncated) {
                toast.warning(toastCopy.exportedChangeEventsTruncated(result.rows.length, result.total));
            } else {
                toast.success(toastCopy.exportedChangeEvents(result.rows.length));
            }
        } catch (exportError) {
            toast.error(exportError instanceof Error ? exportError.message : toastCopy.exportFailed);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <section className="space-y-4">
            <div className="flex items-start gap-2">
                <History className="mt-0.5 w-4 h-4 text-sky-600 shrink-0" />
                <div>
                    <h2 className="font-medium text-sm">Change timeline</h2>
                    <p className="text-muted-foreground text-xs">
                        Append-only audit trail: every insert, update, and delete on tracked tables. Click a row for
                        field-level diffs. All times UTC.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap justify-between items-center gap-2">
                <p className="text-muted-foreground text-sm">
                    {resolvedScope?.diaryScopeEmpty
                        ? "No diaries match the active scope"
                        : data
                          ? `${data.total} change event${data.total === 1 ? "" : "s"}`
                          : "Loading change events"}
                    {data && data.total > 0 ? ` - page ${page + 1} of ${totalPages}` : ""}
                    {isFetching && !isLoading ? " - refreshing" : ""}
                </p>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={exportCurrentPage}
                        disabled={!data?.rows.length || isFetching}
                    >
                        <Download className="mr-1.5 w-4 h-4" />
                        Export page CSV
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void exportAllFiltered()}
                        disabled={isExporting || isFetching || resolvedScope?.diaryScopeEmpty}
                    >
                        <Download className="mr-1.5 w-4 h-4" />
                        {isExporting ? "Exporting..." : "Export timeline CSV"}
                    </Button>
                </div>
            </div>

            {isLoading && (
                <div className="flex justify-center p-8">
                    <Spinner />
                </div>
            )}

            {error && (
                <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                    Error loading change timeline: {error instanceof Error ? error.message : "Unknown error"}
                </div>
            )}

            {resolvedScope?.diaryScopeEmpty && !isLoading && (
                <div className="bg-amber-50 p-4 border border-amber-200 rounded-md text-amber-900 text-sm">
                    No diaries match the active scope. Adjust organisations, labels, or diary ids above, then click
                    Apply filters.
                </div>
            )}

            {data && data.rows.length === 0 && !isLoading && !resolvedScope?.diaryScopeEmpty && (
                <div className="bg-slate-50 p-4 border rounded-md text-muted-foreground text-sm">
                    No change events match these filters.
                </div>
            )}

            {data && data.rows.length > 0 && (
                <AuditLogTable
                    rows={data.rows}
                    onRowClick={(row) => {
                        setSelectedRow(row);
                        setDetailOpen(true);
                    }}
                />
            )}

            {data && data.total > 0 && (
                <div className="flex justify-between items-center">
                    <p className="text-muted-foreground text-sm">
                        Showing rows {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, data.total)} of{" "}
                        {data.total}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((current) => Math.max(0, current - 1))}
                            disabled={page === 0 || isFetching}
                        >
                            <ChevronLeft className="mr-1 w-4 h-4" />
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((current) => current + 1)}
                            disabled={page + 1 >= totalPages || isFetching}
                        >
                            Next
                            <ChevronRight className="ml-1 w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}

            <AuditLogDetailSheet row={selectedRow} open={detailOpen} onOpenChange={setDetailOpen} />
        </section>
    );
}
